// Built-in tintable avatars — the 24 avatar templates recovered from the
// original Flash chat (chat71.swf, see mockup/chat71-assets/). Each avatar is
// two stacked 42×42 bitmaps: a grayscale face/lineart layer drawn on top of a
// flat silhouette ("bg") layer. The legacy engine colored an avatar by flat-
// filling the bg silhouette with the user's chosen color (ColorTransform with
// zero multipliers + RGB offsets — alpha preserved) and leaving the face layer
// untouched. We reproduce that exactly with an SVG filter: feFlood the color,
// composite "in" the bg layer's alpha, then draw the face over it.
//
// A chosen avatar is persisted in the existing User.avatarUrl field as a spec
// string: "builtin:<NN>:<rrggbb>" (NN = 01–24, lowercase hex, no '#'). The
// backend set-avatar handler validates that exact shape. resolveAvatarUrl()
// turns a spec into a self-contained SVG data URL synchronously, so every
// existing <Avatar src> render site works with no changes.
//
// The PNGs are ~1–2.5 KB each, safely under CRA's 10 KB asset-inline limit, so
// the imports below resolve to data: URLs — required, because an SVG loaded
// via <img src> may not reference external resources. (If a larger template
// is ever added, it would resolve to a file URL and silently render blank.)

// Load the 24 face + 24 bg images (Avatar_NN_big.png / Avatar_NN_big_bg.png).
const ctx = require.context('../assets/avatars', false, /Avatar_\d{2}_big(_bg)?\.png$/);
const faces = {};
const bgs = {};
ctx.keys().forEach((key) => {
  const m = key.match(/Avatar_(\d{2})_big(_bg)?\.png$/);
  if (!m) return;
  const mod = ctx(key);
  const url = mod && mod.default ? mod.default : mod;
  if (m[2]) bgs[m[1]] = url;
  else faces[m[1]] = url;
});

// "01".."24", in order. This is the full render/parse set — the picker uses
// pickableBuiltinAvatarIds() instead, which applies the staff gate.
export const BUILTIN_AVATAR_IDS = Object.keys(faces).sort();

// The cop avatars (male/female) are reserved for ADMIN/OWNER, exactly like the
// legacy chat (ColorAvatarPopup.ShowAdmins prepended "13"/"14" for staff only).
// The backend set-avatar handler enforces this; hiding them here is just UX.
export const STAFF_BUILTIN_AVATAR_IDS = ['13', '14'];

// Avatar ids offered in the picker for a user: staff get the cop avatars
// prepended (legacy ShowAdmins order), everyone else doesn't see them at all.
export const pickableBuiltinAvatarIds = (isStaff) => {
  const regular = BUILTIN_AVATAR_IDS.filter(
    (id) => !STAFF_BUILTIN_AVATAR_IDS.includes(id)
  );
  return isStaff ? [...STAFF_BUILTIN_AVATAR_IDS, ...regular] : regular;
};

// The legacy ColorPopup palette, verbatim (10 columns × 5 shades each; column
// order: gray, red, orange, green, spring, teal, azure, blue, purple, pink).
// Pure black is stored as #111111 because the legacy engine remapped color 0
// to 0x111111 everywhere (0 was its "unset" sentinel).
export const BUILTIN_AVATAR_PALETTE = [
  ['#111111', '#dbdbdb', '#999999', '#666666', '#333333'],
  ['#ff0000', '#ff6666', '#ff3333', '#cc0000', '#990000'],
  ['#ff9600', '#eab600', '#cc9933', '#cc6600', '#844a11'],
  ['#66cc00', '#99cc33', '#669900', '#336600', '#1e3e04'],
  ['#00cc33', '#33cc66', '#339933', '#006633', '#00441d'],
  ['#009999', '#00cccc', '#339999', '#006666', '#003333'],
  ['#0099cc', '#33ccff', '#3399cc', '#006699', '#003366'],
  ['#0066ff', '#0099ff', '#0066cc', '#0033cc', '#003399'],
  ['#9933ff', '#9999ff', '#6666ff', '#6633ff', '#6600cc'],
  ['#ff3366', '#ff6699', '#ff3399', '#cc0066', '#990066'],
];

// The legacy default: a new picker with no prior choice starts red (the
// AvatarItemView default tint, 0xFF0000).
export const DEFAULT_BUILTIN_COLOR = 'ff0000';

// The house default avatar for users who never picked/uploaded one: template
// 01, tinted per active theme (palette.custom.avatarDefaultTint — blue on
// midnight, leaf green on sesh). Rendered by <AvatarFallback>.
export const DEFAULT_BUILTIN_AVATAR_ID = '01';

// Normalize a color to the stored 6-digit lowercase hex (no '#'). Applies the
// legacy 0 → 0x111111 remap. Returns null if it isn't a usable hex color.
export const normalizeBuiltinColor = (color) => {
  const hex = String(color || '').replace(/^#/, '').toLowerCase();
  if (!/^[0-9a-f]{6}$/.test(hex)) return null;
  return hex === '000000' ? '111111' : hex;
};

export const isBuiltinAvatarSpec = (value) =>
  typeof value === 'string' && value.startsWith('builtin:');

// "builtin:05:ff6600" → { id: '05', hex: 'ff6600' }, or null if malformed /
// unknown id (e.g. a spec persisted by a newer client with more templates).
export const parseBuiltinAvatarSpec = (value) => {
  const m = /^builtin:(\d{2}):([0-9a-f]{6})$/.exec(value || '');
  if (!m || !faces[m[1]] || !bgs[m[1]]) return null;
  return { id: m[1], hex: m[2] };
};

export const makeBuiltinAvatarSpec = (id, color) => {
  const hex = normalizeBuiltinColor(color);
  return hex ? `builtin:${id}:${hex}` : null;
};

// Compose the two layers into a self-contained SVG data URL: bg silhouette
// flat-filled with the color (the ColorTransform-offset equivalent), face art
// on top. Memoized — render sites call this on every render.
const dataUrlCache = new Map();
export const builtinAvatarDataUrl = (id, color) => {
  const hex = normalizeBuiltinColor(color) || DEFAULT_BUILTIN_COLOR;
  const key = `${id}:${hex}`;
  const cached = dataUrlCache.get(key);
  if (cached) return cached;
  if (!faces[id] || !bgs[id]) return null;
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="42" height="42">' +
    `<filter id="t" color-interpolation-filters="sRGB"><feFlood flood-color="#${hex}"/><feComposite in2="SourceAlpha" operator="in"/></filter>` +
    `<image width="42" height="42" href="${bgs[id]}" filter="url(#t)"/>` +
    `<image width="42" height="42" href="${faces[id]}"/>` +
    '</svg>';
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  dataUrlCache.set(key, url);
  return url;
};

// Spec string → rendered data URL (null if not a valid spec).
export const resolveBuiltinAvatar = (spec) => {
  const parsed = parseBuiltinAvatarSpec(spec);
  return parsed ? builtinAvatarDataUrl(parsed.id, parsed.hex) : null;
};
